import { useCrispIdentify } from '@/hooks/useCrispIdentify';
import { startTokenPrefetch } from '@/lib/token-prefetch';
import { useAuth, useOrganization, useOrganizationList, useUser } from '@clerk/nextjs';
import { useRouter } from 'next/router';
import { usePostHog } from 'posthog-js/react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { GUARD_ROUTES, ORGANIZATION_FREE_ROUTES } from '../config/guard-config';
import { redirectToRoute, shouldRedirect } from '../utils/guard-utils';
import { BaseGuard } from './BaseGuard';
type OrganizationGuardProps = {
  children: ReactNode;
};
const OrganizationGuard = ({
  children
}: OrganizationGuardProps) => {
  const {
    isLoaded: isUserLoaded,
    user
  } = useUser();
  const {
    isLoaded: isOrgLoaded,
    organization
  } = useOrganization();
  const {
    getToken
  } = useAuth();
  const {
    setActive
  } = useOrganizationList();
  const router = useRouter();
  const [hasMembership, setHasMembership] = useState<boolean | null>(null);
  const posthogClient = usePostHog();
  const clerkLoadedTimeRef = useRef<number | null>(null);
  useCrispIdentify(user, organization);
  const isLoaded = isUserLoaded && isOrgLoaded;

  // Log when Clerk hooks become loaded (for perf diagnostics)
  if (isLoaded && !clerkLoadedTimeRef.current) {
    clerkLoadedTimeRef.current = performance.now();
  }

  // Start prefetching the Clerk JWT **during render** (not in an effect)
  // so the promise is available before ClientProvider's effect fires.
  // React runs effects children-first → ClientProvider's useEffect would
  // run before OrganizationGuard's useEffect, making an effect-based
  // prefetch useless. Doing it during render guarantees the promise is
  // ready when ClientProvider consumes it.
  // startTokenPrefetch is idempotent — safe to call on every render.
  if (isLoaded && organization) {
    startTokenPrefetch(getToken, organization.id);
  }

  // Fast path: if Clerk already resolved an active organization, the user
  // definitionally has a membership — resolve synchronously without the
  // async getOrganizationMemberships() API round-trip (~200-500ms saved).
  // The async fallback below only fires for edge cases (new user, removed
  // from all orgs) where organization is null.
  const effectiveHasMembership = isLoaded && organization ? true : hasMembership;
  useEffect(() => {
    const checkOrganization = async () => {
      if (!isLoaded || !user) return;
      const identifiers = {
        environment: process.env.NEXT_PUBLIC_ENV,
        email: user.primaryEmailAddress?.emailAddress,
        firstName: user.firstName,
        lastName: user.lastName
      };
      posthogClient?.identify(user.id, organization ? {
        ...identifiers,
        organization: organization.name
      } : identifiers);

      // Fast path: active org already confirms membership
      if (organization) {
        setHasMembership(true);
        return;
      }

      // Slow path: no active org — need to check via Clerk API
      const memberships = await user?.getOrganizationMemberships();
      const membershipExists = (memberships?.data?.length ?? 0) > 0;
      setHasMembership(membershipExists);
      if (membershipExists) {
        // Fix the session: set the first membership as active org so
        // subsequent loads use the fast path and all API calls have org context.
        const firstOrgId = memberships!.data[0].organization.id;
        setActive?.({
          organization: firstOrgId
        });
        return;
      }
      if (!membershipExists) {
        // Check for pending invitations (e.g., Google OAuth sign-in with invitation ticket).
        // The invitation may not have been consumed during the OAuth flow.
        try {
          const pendingInvitations = await user.getOrganizationInvitations({
            status: 'pending'
          });
          if (pendingInvitations.data.length > 0) {
            const accepted = await pendingInvitations.data[0].accept();
            setActive?.({
              organization: accepted.publicOrganizationData.id
            });
            setHasMembership(true);
            return;
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('[OrganizationGuard] Failed to check/accept pending invitations:', e);
        }
        const shouldRedirectUser = shouldRedirect(router.pathname, ORGANIZATION_FREE_ROUTES, membershipExists);
        if (shouldRedirectUser) {
          await redirectToRoute(router, GUARD_ROUTES.organizations.create, router.pathname);
        }
      }
    };
    checkOrganization();
  }, [isLoaded, router.pathname, router, user, posthogClient, organization, getToken, setActive]);

  // Use membership status for access control
  const hasAccess = effectiveHasMembership ?? false;
  return <BaseGuard guardId="organization" isLoading={!isLoaded || effectiveHasMembership === null} shouldAllowAccess={hasAccess} freeRoutes={ORGANIZATION_FREE_ROUTES}>
      {children}
    </BaseGuard>;
};
export { OrganizationGuard };